import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
} from "react";
import { usePageTitle } from "../../../hooks/useMeta";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { updateUser } from "../../../redux/slices/userSlice";
import { toast } from "react-toastify";
import useApi from "../../../hooks/useApi";
import Tabs from "../../tabs/Tabs";
import Profile from "../settings/company/Profile";
import Images from "../settings/company/Images";
import Manager from "../settings/company/Manager";
import Domains from "../settings/company/Domains";
import Connection from "../settings/Connection";
import DangerZone from "../settings/company/DangerZone";
import {
  getCompanySettings,
  updateCompanySettings,
} from "../../../services/companyService";
import {
  getCompanyUserDetail,
  updateCompanyEmployee,
} from "../../../services/userService";
import { putAttemptChangePassword } from "../../../services/authService";
import SettingsContext from "../../../contexts/SettingsContext";
import useBreakpoint from "../../../hooks/useBreakpoint";
import { useParams } from "react-router-dom";
import useLangNavigate from "../../../hooks/useLangNavigate";
import Button from "../../buttons/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/pro-light-svg-icons";
import Spinner from "../../utils/Spinner";

export const CompanySettings = ({ tKey = "dashboard.settings." }) => {
  usePageTitle("companySettings");
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [manager, setManager] = useState({});
  const [companySettings, setCompanySettings] = useState({});
  const [tempManagerSettings, setTempManagerSettings] = useState({});
  const [tempCompanySettings, setTempCompanySettings] = useState({});
  const [tempPassword, setTempPassword] = useState({});
  const { tabName } = useParams();
  const navigate = useLangNavigate();
  const { request: getSettingsRequest, loading: settingsLoading } =
    useApi(getCompanySettings);
  const { request: getManagerRequest, loading: managerLoading } =
    useApi(getCompanyUserDetail);
  const { request: updateSettingsRequest, loading: updateSettingsLoading } =
    useApi(updateCompanySettings);
  const { request: updateManagerRequest, loading: updateManagerLoading } =
    useApi(updateCompanyEmployee);
  const {
    request: attemptChangePasswordRequest,
    loading: updatePasswordLoading,
  } = useApi(putAttemptChangePassword);
  const user = useSelector((state) => state.user);
  const {
    activeTab,
    setActiveTab,
    isValid,
    setIsValid,
    setApplyChanges,
    setHideSettingsButton,
  } = useContext(SettingsContext);
  const { isLtLaptop } = useBreakpoint();

  const handleFormDataChange = useCallback(
    (data) => {
      setIsValid(!!data);
      setTempCompanySettings((old) => {
        return { ...old, ...data };
      });
    },
    [setIsValid, setTempCompanySettings]
  );

  const handleManagerDataChange = useCallback(
    (data) => {
      setIsValid(!!data);
      setTempManagerSettings((old) => {
        return { ...old, ...data };
      });
    },
    [setIsValid, setTempManagerSettings]
  );

  const handlePasswordChange = useCallback(
    (data) => {
      setIsValid(!!data);
      setTempPassword(data);
    },
    [setIsValid, setTempPassword]
  );

  const tabs = useMemo(
    () => [
      {
        name: "profile",
        content: () => (
          <Profile
            data={companySettings}
            onFormDataChange={handleFormDataChange}
          />
        ),
      },
      {
        name: "images",
        content: () => (
          <Images
            data={companySettings}
            onFormDataChange={handleFormDataChange}
          />
        ),
      },
      {
        name: "manager",
        content: () => (
          <Manager data={manager} onFormDataChange={handleManagerDataChange} />
        ),
      },
      {
        name: "domains",
        content: () => <Domains data={companySettings} />,
      },
      {
        name: "connection",
        content: () => <Connection onFormDataChange={handlePasswordChange} />,
      },
      {
        name: "dangerZone",
        content: () => (
          <DangerZone managerUserId={manager?.userId ?? null} />
        ),
        buttonVariant: "danger",
      },
    ],
    [
      companySettings,
      handleFormDataChange,
      handleManagerDataChange,
      handlePasswordChange,
      manager,
    ]
  );

  const fetchSettingsCompany = useCallback(
    async (companyId) => {
      await getSettingsRequest(companyId)
        .then((response) => {
          setCompanySettings(response.data);
          setManager(response.data.manager[0] || null);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    [getSettingsRequest, setCompanySettings]
  );

  const fetchSettingsManager = useCallback(
    async (userId) => {
      await getManagerRequest(userId)
        .then((response) => {
          dispatch(updateUser(response.data));
        })
        .catch((error) => {
          console.log(error);
        });
    },
    [getManagerRequest, dispatch]
  );

  const attemptUpdateProfile = useCallback(async () => {
    await updateSettingsRequest(user.companyId, tempCompanySettings)
      .then(async () => {
        toast.success(t(`${tKey}success`));
        await fetchSettingsCompany(user.companyId);
      })
      .catch((error) => {
        // Handle address validation error
        if (error.message === "ADDRESS_NOT_FOUND") {
          toast.error(t(`${tKey}profile.errors.address.notFound`));
        } else {
          toast.error(
            error.message ||
              t(`${tKey}success`).replace("successfully", "failed")
          );
          console.error(error);
        }
      });
  }, [
    updateSettingsRequest,
    user.companyId,
    tempCompanySettings,
    t,
    tKey,
    fetchSettingsCompany,
  ]);

  const attemptUpdateManager = useCallback(async () => {
    await updateManagerRequest(manager.userId, tempManagerSettings)
      .then(async () => {
        toast.success(t(`${tKey}success`));
        await fetchSettingsManager(manager.userId);
      })
      .catch((error) => {
        toast.error(
          error.message || t(`${tKey}success`).replace("successfully", "failed")
        );
        console.error(error);
      });
  }, [
    updateManagerRequest,
    manager,
    tempManagerSettings,
    t,
    tKey,
    fetchSettingsManager,
  ]);

  const attemptChangePassword = useCallback(async () => {
    await attemptChangePasswordRequest(user.userId, tempPassword)
      .then(() => {
        toast.success(t(`${tKey}success`));
      })
      .catch((error) => {
        toast.error(
          error.message || t(`${tKey}success`).replace("successfully", "failed")
        );
      });
  }, [attemptChangePasswordRequest, user.userId, tempPassword, t, tKey]);

  const applyChanges = useCallback(() => {
    switch (activeTab) {
      case "manager":
        return manager ? attemptUpdateManager() : null;
      case "connection":
        return attemptChangePassword();
      case "domains":
        return;
      case "dangerZone":
        return;
      default:
        return attemptUpdateProfile();
    }
  }, [
    activeTab,
    manager,
    attemptUpdateProfile,
    attemptUpdateManager,
    attemptChangePassword,
  ]);

  const setActiveTabWrapper = useCallback(
    (tab) => {
      setHideSettingsButton(tab === "dangerZone" || tab === "domains");
      setActiveTab(tab);
    },
    [setActiveTab, setHideSettingsButton]
  );

  const visibleTabNames = useMemo(
    () => tabs.filter((tab) => !tab.hide).map((tab) => tab.name),
    [tabs]
  );

  const defaultTab = useMemo(() => {
    if (visibleTabNames.includes("profile")) return "profile";
    return visibleTabNames[0] || null;
  }, [visibleTabNames]);

  const resolvedTab = useMemo(() => {
    if (!defaultTab) return null;
    return tabName && visibleTabNames.includes(tabName) ? tabName : defaultTab;
  }, [tabName, visibleTabNames, defaultTab]);

  const handleTabChange = useCallback(
    (tab) => {
      setActiveTabWrapper(tab);
      navigate(`/dashboard/settings/${tab}`);
    },
    [setActiveTabWrapper, navigate]
  );

  useEffect(() => {
    fetchSettingsCompany(user.companyId);
    setHideSettingsButton(false);
    return () => {
      setHideSettingsButton(true);
    };
  }, [
    user.companyId,
    fetchSettingsCompany,
    setHideSettingsButton,
  ]);

  useEffect(() => {
    if (!resolvedTab) return;
    setActiveTabWrapper(resolvedTab);
    if (tabName !== resolvedTab) {
      navigate(`/dashboard/settings/${resolvedTab}`, { replace: true });
    }
  }, [resolvedTab, tabName, setActiveTabWrapper, navigate]);

  useEffect(() => {
    setApplyChanges(() => applyChanges);
  }, [applyChanges, setApplyChanges]);

  useEffect(() => {
    if (updateSettingsLoading || updateManagerLoading || updatePasswordLoading)
      setIsValid(false);
  }, [
    updateSettingsLoading,
    updateManagerLoading,
    updatePasswordLoading,
    setIsValid,
  ]);

  if (settingsLoading || managerLoading) return <Spinner />;

  return (
    <div id="settings" className="dashboard-content">
      <Tabs
        preventSwitchTab={false}
        tKey={tKey}
        tKeySuffix=".tab"
        tabs={tabs}
        activeTab={resolvedTab || undefined}
        onTabChange={handleTabChange}
        contentClass="flex gap-1 settings-content"
      ></Tabs>
      {isLtLaptop() && (
        <Button
          onClick={applyChanges}
          floating
          variant="success"
          disabled={!isValid}
          className="flex gap-1"
        >
          <FontAwesomeIcon size="2x" icon={faSave} />
          <span>{t(`utils.common.save`)}</span>
        </Button>
      )}
    </div>
  );
};

export default CompanySettings;
